// src/App.js
import React, { useState, useEffect } from 'react';
import { Loader, AlertCircle } from 'lucide-react';
import authService from './authentication/AuthService';
import Login from './authentication/Login';
import Register from './authentication/Register';
import FinancialDashboard from './components/FinancialDashboard';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [error, setError] = useState('');

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Subscribe to authentication state changes
        const unsubscribe = authService.onAuthStateChange((user) => {
          if (user) {
            setUser(user);
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setIsAuthenticated(false);
          }
          setLoading(false);
        });

        // Return cleanup function
        return unsubscribe;
      } catch (error) {
        console.error('Auth initialization error:', error);
        setError('Failed to initialize authentication');
        setLoading(false);
      }
    };

    const cleanup = initializeAuth();
    
    // Cleanup on component unmount
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, []);

  // Handle successful login
  const handleLogin = (user) => {
    console.log('User logged in:', user);
    // The authService.onAuthStateChange will handle setting the user state
  };

  // Handle successful registration
  const handleRegister = (user) => {
    console.log('User registered:', user);
    // The authService.onAuthStateChange will handle setting the user state
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      setLoading(true);
      const result = await authService.signOut();
      
      if (!result.success) {
        setError('Failed to logout. Please try again.');
        setLoading(false);
      }
      // If successful, the auth state change will handle the rest
    } catch (error) {
      console.error('Logout error:', error);
      setError('An error occurred during logout.');
      setLoading(false);
    }
  };

  // Switch between login and register modes
  const switchToRegister = () => {
    setAuthMode('register');
    setError('');
  };

  const switchToLogin = () => {
    setAuthMode('login');
    setError('');
  };

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen mesh flex items-center justify-center">
        <div className="text-center px-6">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="pulse-dot" aria-hidden="true" />
            <span className="eyebrow">Authenticating</span>
          </div>
          <Loader className="animate-spin text-arc-gold mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="headline text-2xl mb-2">Connecting</h2>
          <p className="text-arc-muted text-sm">Initializing your financial dashboard…</p>
        </div>
      </div>
    );
  }

  // Error screen
  if (error && !isAuthenticated) {
    return (
      <div className="min-h-screen mesh flex items-center justify-center p-4">
        <div className="surface max-w-md w-full p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-arc-line bg-white/5 mb-5">
            <AlertCircle className="text-arc-light" size={28} strokeWidth={1.75} />
          </div>
          <h2 className="headline text-xl mb-3">Something went wrong</h2>
          <p className="text-arc-muted text-sm mb-6">{error}</p>
          <button
            onClick={() => {
              setError('');
              setLoading(true);
              window.location.reload();
            }}
            className="btn-tactile btn-gold px-6 py-2.5 rounded-xl text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main app render - if user is authenticated, show dashboard
  if (isAuthenticated && user) {
    return (
      <div className="App">
        <FinancialDashboard 
          user={user}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  // Authentication screens
  if (authMode === 'register') {
    return (
      <div className="App">
        <Register 
          onRegister={handleRegister}
          onSwitchToLogin={switchToLogin}
        />
      </div>
    );
  }

  // Default to login screen
  return (
    <div className="App">
      <Login 
        onLogin={handleLogin}
        onSwitchToRegister={switchToRegister}
      />
    </div>
  );
}

export default App;